import { SENSOR_FUSION_DATA_LAYERS } from '@/types/shared/navigation-tabs.enums';

interface Graph {
  id: string;
  enabled: boolean;
  [key: string]: any;
}

interface Config {
  dataLayers?: Array<{
    id: string;
    graphs?: Graph[];
  }>;
}

interface InitialEnabledGraphs {
  [key: string]: string[];
}

/**
 * Extract graphs from config for a specific data layer
 */
export function getGraphsByDataLayer(config: Config | undefined, dataLayerId: string): Graph[] {
  return config?.dataLayers?.find((el: any) => el.id === dataLayerId)?.graphs ?? [];
}

/**
 * Extract all graph configurations from config
 */
export function extractGraphConfigurations(config: Config | undefined) {
  const sensorGraphs = getGraphsByDataLayer(config, SENSOR_FUSION_DATA_LAYERS.SENSORS);
  const oilGraphs = getGraphsByDataLayer(config, SENSOR_FUSION_DATA_LAYERS.OIL_REPORT);
  const coolantGraphs = getGraphsByDataLayer(config, SENSOR_FUSION_DATA_LAYERS.COOLANT);
  const alarmGraphs = getGraphsByDataLayer(config, SENSOR_FUSION_DATA_LAYERS.ALARMS);
  const spectrumGraphs = getGraphsByDataLayer(config, SENSOR_FUSION_DATA_LAYERS.SPECTRUM);
  const spectrumAnalysisGraphs = getGraphsByDataLayer(config, SENSOR_FUSION_DATA_LAYERS.SPECTRUM_ANALYSIS);
  const waveformGraphs = getGraphsByDataLayer(config, SENSOR_FUSION_DATA_LAYERS.WAVEFORM);

  return {
    sensorGraphs,
    oilGraphs,
    coolantGraphs,
    alarmGraphs,
    spectrumGraphs,
    spectrumAnalysisGraphs,
    waveformGraphs,
  };
}

/**
 * Initialize enabled graphs based on config
 */
export function initializeEnabledGraphs(
  sensorGraphs: Graph[],
  oilGraphs: Graph[],
  coolantGraphs: Graph[]
): InitialEnabledGraphs {
  return {
    [SENSOR_FUSION_DATA_LAYERS.SENSORS]: sensorGraphs.filter((el: any) => el.enabled).map((el: any) => el.id),
    [SENSOR_FUSION_DATA_LAYERS.OIL_REPORT]: oilGraphs.filter((el: any) => el.enabled).map((el: any) => el.id),
    [SENSOR_FUSION_DATA_LAYERS.COOLANT]: coolantGraphs.filter((el: any) => el.enabled).map((el: any) => el.id),
    [SENSOR_FUSION_DATA_LAYERS.SPECTRUM]: coolantGraphs.filter((el: any) => el.enabled).map((el: any) => el.id),
    [SENSOR_FUSION_DATA_LAYERS.SPECTRUM_ANALYSIS]: coolantGraphs
      .filter((el: any) => el.enabled)
      .map((el: any) => el.id),
  };
}

/**
 * Create initialization key for tracking graph config changes
 */
export function createInitializationKey(
  selectedComponentId: string | null | undefined,
  selectedPart: string | null | undefined,
  sensorGraphs: Graph[],
  oilGraphs: Graph[],
  coolantGraphs: Graph[]
): string {
  return `${selectedComponentId}-${selectedPart}-${sensorGraphs.length}-${oilGraphs.length}-${coolantGraphs.length}`;
}
